import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Apollo } from 'apollo-angular';

import {
  SIGN_IN,
  SIGN_IN_WITH_MAGIC_LINK,
  SIGN_IN_WITH_EMAIL_MAGIC_LINK,
  SIGN_IN_WITH_SMS_MAGIC_LINK,
  SIGN_UP,
  REGISTER_PASSWORDLESS,
  PASSWORD_RESET_WITH_PASSWORD,
  CONFIRM_PHONE_NUMBER,
  UPDATE_PHONE_NUMBER,
} from '@graphql/mutations/auth.mutations';
import {
  FETCH_CURRENT_USER,
  REQUEST_PASSWORD_RESET,
  SEND_PHONE_NUMBER_CONFIRMATION,
  LIST_USER_INVITE_SOURCES,
  REQUEST_EMAIL_MAGIC_LINK,
  REQUEST_SMS_MAGIC_LINK,
  SEND_EMAIL_CONFIRMATION,
} from '@graphql/queries/auth.queries';
import { USER_UPDATE_SUBSCRIPTION } from '@graphql/subscriptions/auth.subscriptions';

import {
  ISignInInput,
  ISignInRes,
  ISignInWithMagicLinkInput,
  ISignInWithMagicLinkRes,
  ISignInWithEmailMagicLinkInput,
  ISignInWithEmailMagicLinkRes,
  ISignInWithSmsMagicLinkInput,
  ISignInWithSmsMagicLinkRes,
  ISignUpInput,
  ISignUpRes,
  IRegisterPasswordlessInput,
  IRegisterPasswordlessRes,
  IFetchUserData,
  IPasswordResetWithPasswordInput,
  ISendPhoneNumberConfirmationData,
  IConfirmNumberData,
  IConfirmNumberInput,
  IRequestPasswordReset,
  IPasswordResetWithPasswordData,
  IListUserInviteSourcesData,
  IRequestEmailMagicLinkInput,
  IRequestEmailMagicLinkData,
  IRequestSmsMagicLinkInput,
  IRequestSmsMagicLinkData,
  ISubscriptionUserUpdateInput,
  ISubscriptionUserUpdateRes,
  ISendEmailConfirmationData,
} from '@shared/models/auth';
import {
  IUpdatePhoneNumberInput,
  IUpdatePhoneNumberData,
} from '@shared/models/profile';
import {
  IUpdateUserPreferenceData,
  IUserPreferenceData,
  IUserPreferenceInput,
} from '@shared/models/user';
import {
  CREATE_USER_PREFERENCE,
  UPDATE_USER_PREFERENCE,
} from '@graphql/mutations/user.mutation';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private apollo: Apollo) {}

  signInUser(input: ISignInInput): Observable<ISignInRes> {
    return this.apollo
      .mutate({
        mutation: SIGN_IN,
        variables: { ...input },
      })
      .pipe(map((result) => result.data as ISignInRes));
  }

  signInWithMagicLink(
    input: ISignInWithMagicLinkInput
  ): Observable<ISignInWithMagicLinkRes> {
    return this.apollo
      .mutate({
        mutation: SIGN_IN_WITH_MAGIC_LINK,
        variables: input,
      })
      .pipe(map((result) => result.data as ISignInWithMagicLinkRes));
  }

  signInWithEmailMagicLink(
    input: ISignInWithEmailMagicLinkInput
  ): Observable<ISignInWithEmailMagicLinkRes> {
    return this.apollo
      .mutate({
        mutation: SIGN_IN_WITH_EMAIL_MAGIC_LINK,
        variables: input,
      })
      .pipe(map((result) => result.data as ISignInWithEmailMagicLinkRes));
  }

  signInWithSmsMagicLink(
    input: ISignInWithSmsMagicLinkInput
  ): Observable<ISignInWithSmsMagicLinkRes> {
    return this.apollo
      .mutate({
        mutation: SIGN_IN_WITH_SMS_MAGIC_LINK,
        variables: input,
      })
      .pipe(map((result) => result.data as ISignInWithSmsMagicLinkRes));
  }

  signUpUser(input: ISignUpInput): Observable<ISignUpRes> {
    return this.apollo
      .mutate({
        mutation: SIGN_UP,
        variables: input,
      })
      .pipe(map((result) => result.data as ISignUpRes));
  }

  registerPasswordless(
    input: IRegisterPasswordlessInput
  ): Observable<IRegisterPasswordlessRes> {
    return this.apollo
      .mutate({
        mutation: REGISTER_PASSWORDLESS,
        variables: { ...input },
      })
      .pipe(map((result) => result.data as IRegisterPasswordlessRes));
  }

  createUserPreference(
    input: IUserPreferenceInput
  ): Observable<IUserPreferenceData> {
    return this.apollo
      .mutate({
        mutation: CREATE_USER_PREFERENCE,
        variables: input,
      })
      .pipe(map((result) => result.data as IUserPreferenceData));
  }

  updateUserPreference(
    id: string,
    input: IUserPreferenceInput['input']
  ): Observable<IUpdateUserPreferenceData> {
    return this.apollo
      .mutate({
        mutation: UPDATE_USER_PREFERENCE,
        variables: { id, input: input },
      })
      .pipe(map((result) => result.data as IUpdateUserPreferenceData));
  }

  fetchUser(): Observable<IFetchUserData> {
    return this.apollo
      .query({
        query: FETCH_CURRENT_USER,
      })
      .pipe(map((result) => result.data));
  }

  requestPasswordReset(email: string): Observable<IRequestPasswordReset> {
    return this.apollo
      .query({
        query: REQUEST_PASSWORD_RESET,
        variables: { email },
      })
      .pipe(map((result) => result.data as IRequestPasswordReset));
  }

  passwordResetWithPassword(
    input: IPasswordResetWithPasswordInput
  ): Observable<IPasswordResetWithPasswordData> {
    return this.apollo
      .mutate({
        mutation: PASSWORD_RESET_WITH_PASSWORD,
        variables: { email: input.email, input: input.input },
      })
      .pipe(map((result) => result.data as IPasswordResetWithPasswordData));
  }

  sendPhoneNumberConfirmation(): Observable<ISendPhoneNumberConfirmationData> {
    return this.apollo
      .query({
        query: SEND_PHONE_NUMBER_CONFIRMATION,
      })
      .pipe(map((result) => result.data as ISendPhoneNumberConfirmationData));
  }

  confirmPhoneNumber(
    input: IConfirmNumberInput
  ): Observable<IConfirmNumberData> {
    return this.apollo
      .mutate({
        mutation: CONFIRM_PHONE_NUMBER,
        variables: input,
      })
      .pipe(map((result) => result.data as IConfirmNumberData));
  }

  updatePhoneNumber(
    input: IUpdatePhoneNumberInput
  ): Observable<IUpdatePhoneNumberData> {
    return this.apollo
      .mutate({
        mutation: UPDATE_PHONE_NUMBER,
        variables: input,
      })
      .pipe(map((result) => result.data as IUpdatePhoneNumberData));
  }

  listUserInviteSources(): Observable<IListUserInviteSourcesData> {
    return this.apollo
      .query({
        query: LIST_USER_INVITE_SOURCES,
      })
      .pipe(map((result) => result.data as IListUserInviteSourcesData));
  }

  requestEmailMagicLink(
    input: IRequestEmailMagicLinkInput
  ): Observable<IRequestEmailMagicLinkData> {
    return this.apollo
      .query({
        query: REQUEST_EMAIL_MAGIC_LINK,
        variables: { ...input },
      })
      .pipe(map((result) => result.data));
  }

  requestSmsMagicLink(
    input: IRequestSmsMagicLinkInput
  ): Observable<IRequestSmsMagicLinkData> {
    return this.apollo
      .query({
        query: REQUEST_SMS_MAGIC_LINK,
        variables: { ...input },
      })
      .pipe(map((result) => result.data));
  }

  subscribeToUserUpdate(
    variables?: ISubscriptionUserUpdateInput
  ): Observable<ISubscriptionUserUpdateRes> {
    return this.apollo
      .subscribe<{
        userUpdate: ISubscriptionUserUpdateRes;
      }>({
        query: USER_UPDATE_SUBSCRIPTION,
        ...(variables ? { variables } : {}),
      })
      .pipe(
        map((result) => result.data?.userUpdate as ISubscriptionUserUpdateRes)
      );
  }

  sendEmailConfirmation(): Observable<ISendEmailConfirmationData> {
    return this.apollo
      .query({
        query: SEND_EMAIL_CONFIRMATION,
      })
      .pipe(map((result) => result.data));
  }
}
